import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { useEffect } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Lock,
  Unlock,
  BookOpen,
  BarChart3,
  Flame,
  Target,
  TrendingUp,
  CheckCircle2,
  ChevronRight,
  Sparkles,
  Award,
} from "lucide-react";
import Navbar from "../components/Navbar";
import { TRIAL_LIMIT } from "./TrialPage";

const TrialPaywallPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  // Redirect authenticated users to full practice
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/practice", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const benefits = [
    {
      icon: BookOpen,
      title: "Unlimited Practice Library",
      description: "Access 80+ curated passages across all difficulty levels and categories",
    },
    {
      icon: BarChart3,
      title: "Performance Dashboard",
      description: "Track your accuracy, speed, and progress with detailed analytics",
    },
    {
      icon: Target,
      title: "Smart Insights",
      description: "Get AI-powered recommendations to improve your weak areas",
    },
    {
      icon: Flame,
      title: "Streak Tracking",
      description: "Stay motivated with daily streaks and achievements",
    },
    {
      icon: TrendingUp,
      title: "Progress Analytics",
      description: "Visualize your improvement over time with charts and trends",
    },
    {
      icon: Award,
      title: "Exam Mode Practice",
      description: "Practice with passages tailored for SSC, Banking, UPSC, and more",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900">
      <Navbar />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Lock Icon */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 shadow-lg shadow-orange-500/30 mb-6">
            <Lock className="w-10 h-10 text-white" />
          </div>
          
          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 mb-4">
            Trial Complete
          </Badge>
          
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4" data-testid="paywall-title">
            You&apos;ve Completed Your Free Trial!
          </h1>
          
          <p className="text-lg text-slate-400 max-w-xl mx-auto">
            You&apos;ve practiced {TRIAL_LIMIT} passages. Create a free account to unlock unlimited practice and track your progress.
          </p>
        </div>

        {/* Main CTA Card */}
        <Card className="mb-8 border-0 shadow-2xl bg-gradient-to-br from-white to-slate-50 overflow-hidden" data-testid="paywall-cta-card">
          <CardContent className="p-8 md:p-10">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 rounded-full text-emerald-700 text-sm font-medium mb-4">
                <Sparkles className="w-4 h-4" />
                100% Free Forever
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                Unlock Full Access
              </h2>
              <p className="text-slate-600">
                No credit card required. Start improving your reading skills today.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button
                size="lg"
                onClick={() => navigate("/auth", { state: { from: { pathname: "/practice" } } })}
                className="bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white shadow-lg shadow-emerald-500/30 h-14 px-8 text-lg"
                data-testid="paywall-signup-btn"
              >
                <Unlock className="w-5 h-5 mr-2" />
                Create Free Account
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate("/auth", { state: { from: { pathname: "/practice" } } })}
                className="border-slate-300 h-14 px-8 text-lg"
                data-testid="paywall-login-btn"
              >
                Already have an account? Sign In
              </Button>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-4 pt-8 border-t border-slate-200">
              <div className="text-center">
                <p className="text-3xl font-bold text-slate-900">80+</p>
                <p className="text-sm text-slate-600">Passages</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-slate-900">10K+</p>
                <p className="text-sm text-slate-600">Students</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-slate-900">6</p>
                <p className="text-sm text-slate-600">Exam Modes</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Benefits Grid */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-white text-center mb-6">
            What You Get With a Free Account
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {benefits.map((benefit, idx) => (
              <Card
                key={idx}
                className="border-0 bg-white/10 backdrop-blur-sm hover:bg-white/15 transition-colors"
              >
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-sky-500/20 flex items-center justify-center flex-shrink-0">
                      <benefit.icon className="w-5 h-5 text-sky-400" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white text-sm mb-1">
                        {benefit.title}
                      </h4>
                      <p className="text-xs text-slate-400">
                        {benefit.description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Dashboard CTA */}
        <Card className="border-0 bg-gradient-to-r from-indigo-600 to-purple-600 overflow-hidden" data-testid="dashboard-cta-card">
          <CardContent className="p-6 md:p-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center">
                  <BarChart3 className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-1">
                    Want to See Your Dashboard?
                  </h3>
                  <p className="text-indigo-200">
                    Track your progress, view analytics, and get personalized insights.
                  </p>
                </div>
              </div>
              <Button
                size="lg"
                onClick={() => navigate("/auth", { state: { from: { pathname: "/dashboard" } } })}
                className="bg-white text-indigo-700 hover:bg-indigo-50 whitespace-nowrap"
                data-testid="paywall-dashboard-btn"
              >
                Login to Dashboard
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Footer Text */}
        <p className="text-center text-slate-500 text-sm mt-8">
          By creating an account, you agree to our Terms of Service and Privacy Policy.
        </p>
      </main>
    </div>
  );
};

export default TrialPaywallPage;
